import { Address, Country } from 'APIs/fragments';
import {
  MerchantBusinessProfile,
  MerchantBusinessType,
  MerchantCompanyType,
  MerchantIndustry,
  UpdateMerchantBusinessProfileRequest,
} from 'APIs/merchants/merchants.types';

export type MerchantBusinessProfileUpdateFormProps = {
  businessName: string;
  brandName: string;
  website: string;
  supportSite: string;
  businessType: MerchantBusinessType | '';
  companyType?: MerchantCompanyType | '';
  industry: MerchantIndustry | '';
  timeZone: string;
  taxCountry: Country | '';
  taxId: string;
  contactEmailAddress: string;
  supportEmailAddress?: string;
  phoneNumber: string;
  cardStatementName: string;
  smsSenderName: string;
  contactPerson: {
    title: string;
    givenName: string;
    familyName: string;
  };
  address: Address;
  verificationDocs: {
    companyFrontFileId?: string;
    companyBackFileId?: string;
    companyTaxIdFileId?: string;
    partnershipAgreementFileId?: string;
    trustDeedFileId?: string;
    trustDeedAmendmentFileIds?: string[];
    partnershipAgreementFile?: File;
    trustDeedFile?: File;
    trustDeedAmendmentFile?: File;
  };
};

type ToUpdateMerchantBusinessProfileRequest = (
  formData: MerchantBusinessProfileUpdateFormProps,
) => UpdateMerchantBusinessProfileRequest;

type ToMerchantBusinessProfileUpdateFormProps = (
  businessProfile: MerchantBusinessProfile,
) => MerchantBusinessProfileUpdateFormProps;

export const toUpdateMerchantBusinessProfileRequest: ToUpdateMerchantBusinessProfileRequest = (
  formData: MerchantBusinessProfileUpdateFormProps,
): UpdateMerchantBusinessProfileRequest => {
  return {
    timeZone: formData.timeZone,
    businessName: formData.businessName,
    businessType: formData.businessType === '' ? 'company' : formData.businessType,
    companyType: formData.companyType === '' ? undefined : formData.companyType,
    website: formData.website,
    supportSite: formData.supportSite,
    industry: formData.industry === '' ? 'AcRefrigerationRepair' : formData.industry,
    taxCountry: formData.taxCountry === '' ? 'AU' : formData.taxCountry,
    taxId: formData.taxId,
    contactPerson: {
      title: formData.contactPerson.title,
      firstName: formData.contactPerson.givenName,
      lastName: formData.contactPerson.familyName,
    },
    contactEmail: formData.contactEmailAddress,
    supportEmail: formData.supportEmailAddress,
    phoneNo: formData.phoneNumber,
    address: ['AU', 'US'].includes(formData.address.country)
      ? { ...formData.address, city: undefined }
      : { ...formData.address, state: undefined },
    brandName: formData.brandName,
    cardStatementName: formData.cardStatementName,
    smsSenderName: formData.smsSenderName,
    verificationDocs: {
      companyBackFileId: formData.verificationDocs.companyBackFileId,
      companyFrontFileId: formData.verificationDocs.companyFrontFileId,
      companyTaxIdFileId: formData.verificationDocs.companyTaxIdFileId,
      partnershipAgreementFileId: formData.verificationDocs.partnershipAgreementFileId,
      trustDeedFileId: formData.verificationDocs.trustDeedFileId,
      trustDeedAmendmentFileIds: formData.verificationDocs.trustDeedAmendmentFileIds,
    },
  };
};

export const toMerchantBusinessProfileUpdateFormProps: ToMerchantBusinessProfileUpdateFormProps = (
  businessProfile: MerchantBusinessProfile,
): MerchantBusinessProfileUpdateFormProps => {
  return {
    businessName: businessProfile.businessName,
    brandName: businessProfile.brandName,
    website: businessProfile.website,
    supportSite: businessProfile.supportSite,
    businessType: businessProfile.businessType,
    companyType: businessProfile.companyType,
    industry: businessProfile.industry,
    timeZone: businessProfile.timeZone,
    taxCountry: businessProfile.taxCountry,
    taxId: businessProfile.taxId ?? '',
    contactEmailAddress: businessProfile.contactEmailAddress,
    supportEmailAddress: businessProfile.supportEmailAddress,
    phoneNumber: businessProfile.phoneNumber,
    cardStatementName: businessProfile.cardStatementName,
    smsSenderName: businessProfile.smsSenderName,
    contactPerson: {
      title: businessProfile.contactPerson.title,
      givenName: businessProfile.contactPerson.givenName,
      familyName: businessProfile.contactPerson.familyName,
    },
    address: businessProfile.address,
    verificationDocs: {
      companyFrontFileId: businessProfile.verificationDocs.companyFrontFileId,
      companyBackFileId: businessProfile.verificationDocs.companyBackFileId,
      companyTaxIdFileId: businessProfile.verificationDocs.companyTaxIdFileId,
      partnershipAgreementFileId: businessProfile.verificationDocs.partnershipAgreementFileId,
      trustDeedFileId: businessProfile.verificationDocs.trustDeedFileId,
      trustDeedAmendmentFileIds: businessProfile.verificationDocs.trustDeedAmendmentFileIds,
    },
  };
};
